import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  Calendar,
  CreditCard,
  ExternalLink,
  Hash,
  Loader2,
  Mail,
  Package,
  Phone,
  Smartphone,
  User,
  X,
} from "lucide-react";
import moment from "moment";
import { PaymentsService } from "../../requests/admin/PaymentsService";
import PaymentStatusBadge from "./PaymentStatusBadge";
import OrderStatusBadge from "../Orders/OrderStatusBadge";
import { formatCurrency } from "../../utils/formatCurrency";
import { formatLabel } from "../../utils/paymentHelpers";

const PaymentDetailsModal = ({ isOpen, paymentCode, initialPayment, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!isOpen || !paymentCode) return undefined;

    let isMounted = true;
    setDetails(null);
    setErrorMessage("");

    const fetchDetails = async () => {
      setIsLoading(true);
      const result = await PaymentsService.getPaymentDetails(paymentCode);
      if (!isMounted) return;

      if (result.success && result.data?.status_code === "000" && !result.data?.in_error) {
        setDetails(result.data.data);
      } else {
        setErrorMessage(result.data?.reason || result.message || "Failed to load payment details");
      }
      setIsLoading(false);
    };

    fetchDetails();
    return () => {
      isMounted = false;
    };
  }, [isOpen, paymentCode]);

  if (!isOpen) return null;

  const payment = details || initialPayment;
  const order = payment?.order;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-[1000] backdrop-blur-sm bg-black/40"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: "spring", duration: 0.5 }}
        className="flex overflow-auto fixed inset-0 z-[1001] justify-center items-center p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative my-8 w-full max-w-3xl bg-white rounded-2xl shadow-2xl">
          <div className="flex sticky top-0 z-10 gap-3 items-start px-6 py-4 bg-white rounded-t-2xl border-b border-gray-100">
            <div className="h-9 w-1 bg-gradient-to-b from-emerald-500 to-teal-600 rounded-full flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap gap-2 items-center">
                <h3 className="text-lg font-semibold text-gray-900">Payment Details</h3>
                {payment?.status && <PaymentStatusBadge status={payment.status} />}
              </div>
              <div className="flex gap-1.5 items-center mt-1 text-xs text-gray-500">
                <Hash className="w-3 h-3" />
                <span className="font-mono">{paymentCode}</span>
              </div>
            </div>
            <button type="button" onClick={onClose} className="p-1.5 rounded-full transition-colors hover:bg-gray-100">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="px-6 py-5 max-h-[calc(100vh-14rem)] overflow-y-auto">
            {isLoading ? (
              <div className="flex flex-col gap-3 justify-center items-center py-16">
                <Loader2 className="w-7 h-7 text-emerald-500 animate-spin" />
                <p className="text-sm text-gray-500">Loading payment details...</p>
              </div>
            ) : errorMessage ? (
              <div className="flex flex-col gap-3 justify-center items-center py-16 text-center">
                <AlertTriangle className="w-10 h-10 text-red-500" />
                <p className="max-w-sm text-sm text-gray-600">{errorMessage}</p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="p-5 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border border-emerald-100">
                  <p className="text-xs font-semibold tracking-wide text-emerald-600 uppercase">Amount Paid</p>
                  <p className="mt-1 text-3xl font-bold text-gray-900">{formatCurrency(payment?.amount)}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-white rounded-full">
                      <Smartphone className="w-3 h-3" />
                      {payment?.network || "—"} · {formatLabel(payment?.payment_method)}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-white rounded-full">
                      <CreditCard className="w-3 h-3" />
                      {formatLabel(payment?.purpose)}
                    </span>
                  </div>
                </div>

                {payment?.user && (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {[
                      { icon: User, label: "Customer", value: payment.user.full_name },
                      { icon: Mail, label: "Email", value: payment.email || payment.user.email },
                      { icon: Phone, label: "Phone", value: payment.phone_number || payment.user.phone_number },
                    ].map(({ icon: Icon, label, value }) => (
                      <div key={label} className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                        <div className="flex gap-2 items-center mb-1 text-gray-400">
                          <Icon className="w-3.5 h-3.5" />
                          <span className="text-[11px] font-semibold uppercase">{label}</span>
                        </div>
                        <p className="text-sm font-medium text-gray-900 truncate">{value || "—"}</p>
                      </div>
                    ))}
                  </div>
                )}

                {order && (
                  <div className="p-4 bg-white rounded-xl border border-gray-100">
                    <div className="flex flex-wrap gap-2 items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-700">Linked Order</h4>
                      <OrderStatusBadge status={order.status} />
                    </div>
                    <p className="font-mono text-sm text-gray-600">{order.order_code}</p>
                    <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-500">
                      <span>{formatLabel(order.fulfillment_type)}</span>
                      <span>{formatLabel(order.purchase_type)}</span>
                      <span className="font-semibold text-gray-800">{formatCurrency(order.total_amount)}</span>
                    </div>
                  </div>
                )}

                {order?.items?.length > 0 && (
                  <div>
                    <div className="flex gap-2 items-center mb-3">
                      <Package className="w-4 h-4 text-gray-500" />
                      <h4 className="text-sm font-semibold text-gray-700">Order Items</h4>
                    </div>
                    <div className="space-y-2">
                      {order.items.map((item, idx) => {
                        const size =
                          item.size != null && String(item.size).trim()
                            ? String(item.size).trim()
                            : null;
                        return (
                        <div
                          key={`${item.deal_slug || "item"}-${size || "nosize"}-${idx}`}
                          className="flex gap-3 items-center p-3 bg-gray-50 rounded-xl"
                        >
                          <img
                            src={item.deal_image}
                            alt={item.deal_title}
                            className="w-12 h-12 rounded-lg object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{item.deal_title}</p>
                            <p className="text-xs text-gray-500 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                              {size ? (
                                <span className="inline-flex items-center rounded-md bg-gray-100 px-1.5 py-0.5 text-[11px] font-bold text-gray-700">
                                  Size {size}
                                </span>
                              ) : null}
                              <span>
                                {item.quantity} × {formatCurrency(item.unit_price)}
                              </span>
                            </p>
                          </div>
                          <p className="text-sm font-semibold">{formatCurrency(item.line_total)}</p>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-4 text-xs text-gray-400">
                  <span className="flex gap-1.5 items-center">
                    <Calendar className="w-3.5 h-3.5" />
                    Created {moment(payment?.created_at).format("DD MMM YYYY, hh:mm A")}
                  </span>
                  {payment?.paid_at && (
                    <span className="flex gap-1.5 items-center text-green-600">
                      Paid {moment(payment.paid_at).format("DD MMM YYYY, hh:mm A")}
                    </span>
                  )}
                </div>

                {payment?.payment_link && (
                  <a
                    href={payment.payment_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex gap-2 items-center px-4 py-2.5 text-sm font-medium text-blue-700 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open Payment Gateway Link
                  </a>
                )}
              </div>
            )}
          </div>

          <div className="flex sticky bottom-0 justify-end px-6 py-4 bg-white rounded-b-2xl border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default PaymentDetailsModal;
